import React, { useState, useEffect, useRef } from 'react';
import { FiSave, FiX, FiImage, FiBox, FiTag, FiTruck, FiGlobe, FiLayers, FiList, FiTrash2, FiPlus } from 'react-icons/fi';
import { getImageUrl } from '../../utils/imageUtils';
import '../../styles/Admin.css';

const ProductEditor = ({ product, onSave, onCancel, uploading, onUpload }) => {
    const [activeTab, setActiveTab] = useState('basic');
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: 'textiles',
        subcategory: '',
        brand: '',
        price: '',
        originalPrice: '',
        discount: 0,
        taxRate: 0,
        sku: '',
        barcode: '',
        stockQuantity: 0,
        lowStockThreshold: 10,
        inStock: true,
        isLimitedTimeDeal: false,
        images: [],
        videoUrl: '',
        weight: '',
        dimensions: { length: '', width: '', height: '' },
        shippingClass: 'standard',
        slug: '',
        metaTitle: '',
        metaDescription: '',
        details: [], // For "About this item" bullets,
        itemDetails: [], // Detailed key-value list
        measurements: [], // Array of { key, value }
        tags: '',
        visibility: 'active',
        variants: [],
        customAttributes: [],
        specsVisibility: {
            itemDetails: true,
            measurements: true,
            features: true
        },
        specsTitles: {
            itemDetails: 'Item Details',
            measurements: 'Measurements',
            features: 'Features & Specs'
        }
    });

    const [currentImageInput, setCurrentImageInput] = useState('');

    // Refs for scrolling to new items
    const bulletsContainerRef = useRef(null);
    const itemDetailsContainerRef = useRef(null);
    const measurementsContainerRef = useRef(null);
    const featuresContainerRef = useRef(null);

    // Refs to track previous lengths to trigger scroll only on addition
    const prevDetailsLengthRef = useRef(0);
    const prevItemDetailsLengthRef = useRef(0);
    const prevMeasurementsLengthRef = useRef(0);
    const prevFeaturesLengthRef = useRef(0);

    // Auto-scroll effects
    useEffect(() => {
        const currentLength = formData.details?.length || 0;
        if (currentLength > prevDetailsLengthRef.current && bulletsContainerRef.current) {
            const lastChild = bulletsContainerRef.current.lastElementChild;
            if (lastChild) lastChild.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        prevDetailsLengthRef.current = currentLength;
    }, [formData.details?.length]);

    useEffect(() => {
        const currentLength = formData.itemDetails?.length || 0;
        if (currentLength > prevItemDetailsLengthRef.current && itemDetailsContainerRef.current) {
            const lastChild = itemDetailsContainerRef.current.lastElementChild;
            if (lastChild) lastChild.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        prevItemDetailsLengthRef.current = currentLength;
    }, [formData.itemDetails?.length]);

    useEffect(() => {
        const currentLength = formData.measurements?.length || 0;
        if (currentLength > prevMeasurementsLengthRef.current && measurementsContainerRef.current) {
            const lastChild = measurementsContainerRef.current.lastElementChild;
            if (lastChild) lastChild.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        prevMeasurementsLengthRef.current = currentLength;
    }, [formData.measurements?.length]);

    useEffect(() => {
        const currentLength = formData.customAttributes?.length || 0;
        if (currentLength > prevFeaturesLengthRef.current && featuresContainerRef.current) {
            const lastChild = featuresContainerRef.current.lastElementChild;
            if (lastChild) lastChild.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        prevFeaturesLengthRef.current = currentLength;
    }, [formData.customAttributes?.length]);

    useEffect(() => {
        if (product) {
            setFormData(prev => ({
                ...prev,
                ...product,
                // Ensure nested objects exist
                dimensions: product.dimensions || { length: '', width: '', height: '' },
                images: product.images || (product.image ? [product.image] : []),
                customAttributes: product.customAttributes || [],
                details: product.details || [],
                itemDetails: Array.isArray(product.itemDetails) ? product.itemDetails : [], // Handle potential legacy string
                measurements: product.measurements || [],
                isLimitedTimeDeal: product.isLimitedTimeDeal || false,
                taxRate: product.taxRate || 0,
                specsVisibility: product.specsVisibility || {
                    itemDetails: true,
                    measurements: true,
                    features: true
                },
                specsTitles: product.specsTitles || {
                    itemDetails: 'Item Details',
                    measurements: 'Measurements',
                    features: 'Features & Specs'
                }
            }));
        }
    }, [product]);

    // Helper to determine dropdown value
    const getTitleOption = (currentTitle, options) => {
        if (currentTitle === undefined || currentTitle === null) return options[0]; // Default
        return options.includes(currentTitle) ? currentTitle : 'Custom';
    };

    // Predefined options
    const titleOptions = {
        itemDetails: ['Item Details', 'Product Information', 'About This Item'],
        measurements: ['Measurements', 'Dimensions', 'Size Guide'],
        features: ['Features & Specs', 'Specifications', 'Technical Details']
    };

    const handleTitleChange = (section, newValue, isCustomInput = false) => {
        setFormData(prev => {
            const currentTitles = prev.specsTitles || {};
            // If selecting from dropdown (not custom), set directly. 
            // If typing in custom input, set directly (newValue is already the final string).
            // If switching TO custom from dropdown, we handle that in the onChange by clearing or setting a default.

            return {
                ...prev,
                specsTitles: {
                    ...currentTitles,
                    [section]: newValue
                }
            };
        });
    };

    const handleAttributeChange = (index, field, value) => {
        const newAttributes = [...formData.customAttributes];
        newAttributes[index] = { ...newAttributes[index], [field]: value };
        setFormData(prev => ({ ...prev, customAttributes: newAttributes }));
    };

    const handleAddAttribute = () => {
        setFormData(prev => ({
            ...prev,
            customAttributes: [...(prev.customAttributes || []), { key: '', value: '' }]
        }));
    };

    const handleRemoveAttribute = (index) => {
        setFormData(prev => ({
            ...prev,
            customAttributes: prev.customAttributes.filter((_, i) => i !== index)
        }));
    };

    const handleDetailChange = (index, value) => {
        const newDetails = [...formData.details];
        newDetails[index] = value;
        setFormData(prev => ({ ...prev, details: newDetails }));
    };

    const handleAddDetail = () => {
        setFormData(prev => ({
            ...prev,
            details: [...(prev.details || []), '']
        }));
    };

    const handleRemoveDetail = (index) => {
        setFormData(prev => ({
            ...prev,
            details: prev.details.filter((_, i) => i !== index)
        }));
    };

    const handleItemDetailChange = (index, field, value) => {
        const newItemDetails = [...formData.itemDetails];
        newItemDetails[index] = { ...newItemDetails[index], [field]: value };
        setFormData(prev => ({ ...prev, itemDetails: newItemDetails }));
    };

    const handleAddItemDetail = () => {
        setFormData(prev => ({
            ...prev,
            itemDetails: [...(prev.itemDetails || []), { key: '', value: '' }]
        }));
    };

    const handleRemoveItemDetail = (index) => {
        setFormData(prev => ({
            ...prev,
            itemDetails: prev.itemDetails.filter((_, i) => i !== index)
        }));
    };

    const handleMeasurementChange = (index, field, value) => {
        const newMeasurements = [...formData.measurements];
        newMeasurements[index] = { ...newMeasurements[index], [field]: value };
        setFormData(prev => ({ ...prev, measurements: newMeasurements }));
    };

    const handleAddMeasurement = () => {
        setFormData(prev => ({
            ...prev,
            measurements: [...(prev.measurements || []), { key: '', value: '' }]
        }));
    };

    const handleRemoveMeasurement = (index) => {
        setFormData(prev => ({
            ...prev,
            measurements: prev.measurements.filter((_, i) => i !== index)
        }));
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleAddImage = () => {
        if (currentImageInput.trim()) {
            setFormData(prev => ({
                ...prev,
                images: [...prev.images, currentImageInput.trim()]
            }));
            setCurrentImageInput('');
        }
    };

    const handleRemoveImage = (index) => {
        setFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const categories = [
        { value: 'textiles', label: 'Textiles & Fabrics' },
        { value: 'pottery', label: 'Pottery & Ceramics' },
        { value: 'handicrafts', label: 'Handicrafts' },
        { value: 'stationary', label: 'Stationary' },
        { value: 'electronics', label: 'Electronics' },
        { value: 'home-decor', label: 'Home Decor' },
        { value: 'woodcraft', label: 'Woodcraft' }
    ];

    const tabs = [
        { id: 'basic', label: 'Basic Info', icon: <FiTag /> },
        { id: 'pricing', label: 'Pricing & Inventory', icon: <FiBox /> },
        { id: 'media', label: 'Media', icon: <FiImage /> },
        { id: 'shipping', label: 'Shipping', icon: <FiTruck /> },
        { id: 'seo', label: 'SEO & Visibility', icon: <FiGlobe /> },
        { id: 'specs', label: 'Specifications', icon: <FiList /> },
        { id: 'variants', label: 'Variants', icon: <FiLayers /> },
    ];

    return (
        <div className="product-editor">
            <div className="editor-header">
                <div className="editor-tabs">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            className={`editor-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.icon} <span>{tab.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            <form onSubmit={handleSubmit} className="editor-form">
                <div className="editor-content">

                    {/* Basic Info Tab */}
                    {activeTab === 'basic' && (
                        <div className="tab-content">
                            {/* Product Name Section */}
                            <div className="pricing-section-card">
                                <div className="pricing-section-header">
                                    <FiTag className="pricing-section-icon" />
                                    <span>Product Name</span>
                                </div>
                                <div className="form-row">
                                    <div className="form-group full-width">
                                        <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="Enter product name" />
                                    </div>
                                </div>
                            </div>

                            {/* Feature Bullets Section */}
                            <div className="pricing-section-card">
                                <div className="pricing-section-header">
                                    <FiList className="pricing-section-icon" />
                                    <span>About This Item</span>
                                    <span className="bullet-count">{1 + (formData.details?.length || 0)} bullets</span>
                                </div>

                                <div className="bullets-container" ref={bulletsContainerRef}>
                                    {/* Main Description as First Bullet */}
                                    <div className="bullet-item">
                                        <span className="bullet-number">1</span>
                                        <input
                                            type="text"
                                            name="description"
                                            value={formData.description}
                                            onChange={handleChange}
                                            required
                                            placeholder="Main product description"
                                            className="bullet-input"
                                        />
                                        <div className="bullet-action-spacer"></div>
                                    </div>

                                    {/* Additional Bullets */}
                                    {formData.details && formData.details.map((detail, index) => (
                                        <div key={index} className="bullet-item">
                                            <span className="bullet-number">{index + 2}</span>
                                            <input
                                                type="text"
                                                value={detail}
                                                onChange={(e) => handleDetailChange(index, e.target.value)}
                                                placeholder="Feature description..."
                                                className="bullet-input"
                                            />
                                            <button
                                                type="button"
                                                className="bullet-delete-btn"
                                                onClick={() => handleRemoveDetail(index)}
                                                title="Remove"
                                            >
                                                <FiX />
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                <button type="button" className="add-bullet-btn" onClick={handleAddDetail}>
                                    <FiPlus /> Add Bullet
                                </button>
                            </div>

                            {/* Classification Section */}
                            <div className="pricing-section-card">
                                <div className="pricing-section-header">
                                    <FiBox className="pricing-section-icon" />
                                    <span>Classification</span>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Category *</label>
                                        <select
                                            name="category"
                                            value={categories.some(c => c.value === formData.category) ? formData.category : 'custom'}
                                            onChange={(e) => {
                                                if (e.target.value === 'custom') {
                                                    setFormData(prev => ({ ...prev, category: '' }));
                                                } else {
                                                    handleChange(e);
                                                }
                                            }}
                                            className="styled-select"
                                        >
                                            {categories.map(c => (
                                                <option key={c.value} value={c.value}>{c.label}</option>
                                            ))}
                                            <option value="custom">Custom...</option>
                                        </select>
                                        {!categories.some(c => c.value === formData.category) && (
                                            <input
                                                type="text"
                                                name="category"
                                                value={formData.category}
                                                onChange={handleChange}
                                                placeholder="Enter custom category"
                                                className="custom-category-input"
                                            />
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label>Subcategory</label>
                                        <input type="text" name="subcategory" value={formData.subcategory} onChange={handleChange} placeholder="e.g., Summer Collection" />
                                    </div>
                                    <div className="form-group">
                                        <label>Brand / Manufacturer</label>
                                        <input type="text" name="brand" value={formData.brand} onChange={handleChange} placeholder="e.g., Kalinga Crafts" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Pricing & Inventory Tab */}
                    {activeTab === 'pricing' && (
                        <div className="tab-content">
                            {/* Pricing Section */}
                            <div className="pricing-section-card">
                                <div className="pricing-section-header">
                                    <FiTag className="pricing-section-icon" />
                                    <span>Pricing</span>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Selling Price (₹) *</label>
                                        <div className="input-with-prefix">
                                            <span className="input-prefix">₹</span>
                                            <input type="number" name="price" value={formData.price} onChange={handleChange} required placeholder="0.00" />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Original Price (₹)</label>
                                        <div className="input-with-prefix">
                                            <span className="input-prefix">₹</span>
                                            <input type="number" name="originalPrice" value={formData.originalPrice} onChange={handleChange} placeholder="0.00" />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Discount</label>
                                        <div className="input-with-suffix">
                                            <input type="number" name="discount" value={formData.discount} onChange={handleChange} placeholder="0" />
                                            <span className="input-suffix">%</span>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>GST Rate</label>
                                        <select name="taxRate" value={formData.taxRate || 0} onChange={handleChange} className="styled-select">
                                            <option value={0}>0% (Exempt)</option>
                                            <option value={5}>5%</option>
                                            <option value={12}>12%</option>
                                            <option value={18}>18%</option>
                                            <option value={28}>28%</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Product Codes Section */}
                            <div className="pricing-section-card">
                                <div className="pricing-section-header">
                                    <FiBox className="pricing-section-icon" />
                                    <span>Product Codes</span>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>SKU (Stock Keeping Unit)</label>
                                        <input type="text" name="sku" value={formData.sku} onChange={handleChange} placeholder="e.g., PROD-001" />
                                    </div>
                                    <div className="form-group">
                                        <label>Barcode (UPC/EAN)</label>
                                        <input type="text" name="barcode" value={formData.barcode} onChange={handleChange} placeholder="e.g., 123456789012" />
                                    </div>
                                </div>
                            </div>

                            {/* Inventory Section */}
                            <div className="pricing-section-card">
                                <div className="pricing-section-header">
                                    <FiLayers className="pricing-section-icon" />
                                    <span>Inventory</span>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Stock Quantity *</label>
                                        <input type="number" name="stockQuantity" value={formData.stockQuantity} onChange={handleChange} required placeholder="0" />
                                    </div>
                                    <div className="form-group">
                                        <label>Low Stock Alert</label>
                                        <input type="number" name="lowStockThreshold" value={formData.lowStockThreshold} onChange={handleChange} placeholder="10" />
                                        <span className="input-hint">Alert when stock falls below this</span>
                                    </div>
                                </div>
                            </div>

                            {/* Status Toggles Section */}
                            <div className="pricing-section-card status-toggles-card">
                                <div className="pricing-section-header">
                                    <FiGlobe className="pricing-section-icon" />
                                    <span>Product Status</span>
                                </div>
                                <div className="status-toggles-grid">
                                    <div className="status-toggle-item">
                                        <div className="status-toggle-info">
                                            <span className="status-toggle-label">In Stock</span>
                                            <span className="status-toggle-hint">Product is available for purchase</span>
                                        </div>
                                        <label className="toggle-switch">
                                            <input type="checkbox" name="inStock" checked={formData.inStock} onChange={handleChange} />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>
                                    <div className="status-toggle-item">
                                        <div className="status-toggle-info">
                                            <span className="status-toggle-label">Limited Time Deal</span>
                                            <span className="status-toggle-hint">Show special deal badge on product</span>
                                        </div>
                                        <label className="toggle-switch">
                                            <input type="checkbox" name="isLimitedTimeDeal" checked={formData.isLimitedTimeDeal || false} onChange={handleChange} />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Media Tab */}
                    {activeTab === 'media' && (
                        <div className="tab-content">
                            <h3>Media</h3>
                            <div className="form-group">
                                <label>Upload Images</label>
                                <input type="file" multiple accept="image/*" onChange={(e) => onUpload(e.target.files)} />
                                {uploading && <p>Uploading...</p>}
                            </div>
                            <div className="form-group">
                                <label>Or Add Image URL</label>
                                <div className="input-group">
                                    <input type="text" value={currentImageInput} onChange={(e) => setCurrentImageInput(e.target.value)} placeholder="https://..." />
                                    <button type="button" onClick={handleAddImage} className="btn-secondary">Add</button>
                                </div>
                            </div>
                            <div className="image-grid">
                                {formData.images.map((url, index) => (
                                    <div key={index} className="image-card">
                                        <img src={getImageUrl(url)} alt={`Product ${index}`} />
                                        <button type="button" onClick={() => handleRemoveImage(index)} className="remove-btn">×</button>
                                    </div>
                                ))}
                            </div>
                            <div className="form-group full-width">
                                <label>Video URL (YouTube/Vimeo)</label>
                                <input type="text" name="videoUrl" value={formData.videoUrl} onChange={handleChange} placeholder="https://youtube.com/..." />
                            </div>
                        </div>
                    )}

                    {/* Shipping Tab */}
                    {activeTab === 'shipping' && (
                        <div className="tab-content">
                            <h3>Shipping Information</h3>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Weight (kg)</label>
                                    <input type="number" name="weight" value={formData.weight} onChange={handleChange} step="0.01" />
                                </div>
                                <div className="form-group">
                                    <label>Shipping Class</label>
                                    <select name="shippingClass" value={formData.shippingClass} onChange={handleChange}>
                                        <option value="standard">Standard</option>
                                        <option value="heavy">Heavy</option>
                                        <option value="fragile">Fragile</option>
                                    </select>
                                </div>
                            </div>
                            <h4>Dimensions (cm)</h4>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Length</label>
                                    <input type="number" name="dimensions.length" value={formData.dimensions.length} onChange={handleChange} />
                                </div>
                                <div className="form-group">
                                    <label>Width</label>
                                    <input type="number" name="dimensions.width" value={formData.dimensions.width} onChange={handleChange} />
                                </div>
                                <div className="form-group">
                                    <label>Height</label>
                                    <input type="number" name="dimensions.height" value={formData.dimensions.height} onChange={handleChange} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Specifications Tab */}
                    {activeTab === 'specs' && (
                        <div className="tab-content">
                            <h3>Product Information</h3>

                            {/* Visibility & Titles Control Section */}
                            <div className="section-config-panel">
                                <div className="section-config-header">
                                    <FiLayers className="section-config-icon" />
                                    <span>Section Configuration</span>
                                </div>
                                <p className="section-config-hint">Control which specification sections appear on the product page and customize their titles.</p>

                                {/* Item Details Config */}
                                <div className="section-config-item">
                                    <div className="section-config-toggle">
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={formData.specsVisibility?.itemDetails}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    specsVisibility: { ...prev.specsVisibility, itemDetails: e.target.checked }
                                                }))}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                        <span className="toggle-label">Show Details Section</span>
                                    </div>

                                    {formData.specsVisibility?.itemDetails && (
                                        <div className="section-config-title-selector">
                                            <select
                                                value={getTitleOption(formData.specsTitles?.itemDetails, titleOptions.itemDetails)}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val !== 'Custom') handleTitleChange('itemDetails', val);
                                                    else handleTitleChange('itemDetails', '');
                                                }}
                                                className="section-title-dropdown"
                                            >
                                                {titleOptions.itemDetails.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                                <option value="Custom">Custom Title...</option>
                                            </select>

                                            {getTitleOption(formData.specsTitles?.itemDetails, titleOptions.itemDetails) === 'Custom' && (
                                                <input
                                                    type="text"
                                                    placeholder="Enter custom title"
                                                    value={formData.specsTitles?.itemDetails || ''}
                                                    onChange={(e) => handleTitleChange('itemDetails', e.target.value, true)}
                                                    className="section-custom-input"
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Measurements Config */}
                                <div className="section-config-item">
                                    <div className="section-config-toggle">
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={formData.specsVisibility?.measurements}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    specsVisibility: { ...prev.specsVisibility, measurements: e.target.checked }
                                                }))}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                        <span className="toggle-label">Show Measurements</span>
                                    </div>

                                    {formData.specsVisibility?.measurements && (
                                        <div className="section-config-title-selector">
                                            <select
                                                value={getTitleOption(formData.specsTitles?.measurements, titleOptions.measurements)}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val !== 'Custom') handleTitleChange('measurements', val);
                                                    else handleTitleChange('measurements', '');
                                                }}
                                                className="section-title-dropdown"
                                            >
                                                {titleOptions.measurements.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                                <option value="Custom">Custom Title...</option>
                                            </select>

                                            {getTitleOption(formData.specsTitles?.measurements, titleOptions.measurements) === 'Custom' && (
                                                <input
                                                    type="text"
                                                    placeholder="Enter custom title"
                                                    value={formData.specsTitles?.measurements || ''}
                                                    onChange={(e) => handleTitleChange('measurements', e.target.value, true)}
                                                    className="section-custom-input"
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Features Config */}
                                <div className="section-config-item">
                                    <div className="section-config-toggle">
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={formData.specsVisibility?.features}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    specsVisibility: { ...prev.specsVisibility, features: e.target.checked }
                                                }))}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                        <span className="toggle-label">Show Features</span>
                                    </div>

                                    {formData.specsVisibility?.features && (
                                        <div className="section-config-title-selector">
                                            <select
                                                value={getTitleOption(formData.specsTitles?.features, titleOptions.features)}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val !== 'Custom') handleTitleChange('features', val);
                                                    else handleTitleChange('features', '');
                                                }}
                                                className="section-title-dropdown"
                                            >
                                                {titleOptions.features.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                                <option value="Custom">Custom Title...</option>
                                            </select>

                                            {getTitleOption(formData.specsTitles?.features, titleOptions.features) === 'Custom' && (
                                                <input
                                                    type="text"
                                                    placeholder="Enter custom title"
                                                    value={formData.specsTitles?.features || ''}
                                                    onChange={(e) => handleTitleChange('features', e.target.value, true)}
                                                    className="section-custom-input"
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Item Details Card */}
                            <div className="pricing-section-card">
                                <div className="pricing-section-header">
                                    <FiTag className="pricing-section-icon" />
                                    <span>{formData.specsTitles?.itemDetails || 'Item Details'}</span>
                                    <span className="bullet-count">{formData.itemDetails?.length || 0} items</span>
                                </div>

                                <div className="kv-list-container" ref={itemDetailsContainerRef}>
                                    {formData.itemDetails && Array.isArray(formData.itemDetails) && formData.itemDetails.map((attr, index) => (
                                        <div key={index} className="kv-item">
                                            <input
                                                type="text"
                                                placeholder="Property"
                                                value={attr.key}
                                                onChange={(e) => handleItemDetailChange(index, 'key', e.target.value)}
                                                className="kv-key-input"
                                            />
                                            <span className="kv-separator">:</span>
                                            <input
                                                type="text"
                                                placeholder="Value"
                                                value={attr.value}
                                                onChange={(e) => handleItemDetailChange(index, 'value', e.target.value)}
                                                className="kv-value-input"
                                            />
                                            <button
                                                type="button"
                                                className="bullet-delete-btn"
                                                onClick={() => handleRemoveItemDetail(index)}
                                            >
                                                <FiX />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <button type="button" className="add-bullet-btn" onClick={handleAddItemDetail}>
                                    <FiPlus /> Add Item
                                </button>
                            </div>

                            {/* Measurements Card */}
                            <div className="pricing-section-card">
                                <div className="pricing-section-header">
                                    <FiBox className="pricing-section-icon" />
                                    <span>{formData.specsTitles?.measurements || 'Measurements'}</span>
                                    <span className="bullet-count">{formData.measurements?.length || 0} items</span>
                                </div>

                                <div className="kv-list-container" ref={measurementsContainerRef}>
                                    {formData.measurements && formData.measurements.map((attr, index) => (
                                        <div key={index} className="kv-item">
                                            <input
                                                type="text"
                                                placeholder="Dimension"
                                                value={attr.key}
                                                onChange={(e) => handleMeasurementChange(index, 'key', e.target.value)}
                                                className="kv-key-input"
                                            />
                                            <span className="kv-separator">:</span>
                                            <input
                                                type="text"
                                                placeholder="Value"
                                                value={attr.value}
                                                onChange={(e) => handleMeasurementChange(index, 'value', e.target.value)}
                                                className="kv-value-input"
                                            />
                                            <button
                                                type="button"
                                                className="bullet-delete-btn"
                                                onClick={() => handleRemoveMeasurement(index)}
                                            >
                                                <FiX />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <button type="button" className="add-bullet-btn" onClick={handleAddMeasurement}>
                                    <FiPlus /> Add Item
                                </button>
                            </div>

                            {/* Features & Specs Card */}
                            <div className="pricing-section-card">
                                <div className="pricing-section-header">
                                    <FiLayers className="pricing-section-icon" />
                                    <span>{formData.specsTitles?.features || 'Features & Specs'}</span>
                                    <span className="bullet-count">{formData.customAttributes?.length || 0} items</span>
                                </div>

                                <div className="kv-list-container" ref={featuresContainerRef}>
                                    {formData.customAttributes && formData.customAttributes.map((attr, index) => (
                                        <div key={index} className="kv-item">
                                            <input
                                                type="text"
                                                placeholder="Name"
                                                value={attr.key}
                                                onChange={(e) => handleAttributeChange(index, 'key', e.target.value)}
                                                className="kv-key-input"
                                            />
                                            <span className="kv-separator">:</span>
                                            <input
                                                type="text"
                                                placeholder="Value"
                                                value={attr.value}
                                                onChange={(e) => handleAttributeChange(index, 'value', e.target.value)}
                                                className="kv-value-input"
                                            />
                                            <button
                                                type="button"
                                                className="bullet-delete-btn"
                                                onClick={() => handleRemoveAttribute(index)}
                                            >
                                                <FiX />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <button type="button" className="add-bullet-btn" onClick={handleAddAttribute}>
                                    <FiPlus /> Add Item
                                </button>
                            </div>
                        </div>
                    )}

                    {/* SEO & Visibility Tab */}
                    {activeTab === 'seo' && (
                        <div className="tab-content">
                            <h3>SEO & Visibility</h3>
                            <div className="form-row">
                                <div className="form-group full-width">
                                    <label>URL Slug</label>
                                    <input type="text" name="slug" value={formData.slug} onChange={handleChange} placeholder="product-name-slug" />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group full-width">
                                    <label>Meta Title</label>
                                    <input type="text" name="metaTitle" value={formData.metaTitle} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group full-width">
                                    <label>Meta Description</label>
                                    <textarea name="metaDescription" value={formData.metaDescription} onChange={handleChange} rows={3} />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Visibility Status</label>
                                    <select name="visibility" value={formData.visibility} onChange={handleChange}>
                                        <option value="active">Active</option>
                                        <option value="draft">Draft</option>
                                        <option value="hidden">Hidden</option>
                                        <option value="archived">Archived</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Tags (comma separated)</label>
                                    <input type="text" name="tags" value={formData.tags} onChange={handleChange} placeholder="cotton, handmade, summer" />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Variants Tab */}
                    {activeTab === 'variants' && (
                        <div className="tab-content">
                            <h3>Product Variants</h3>
                            <p className="info-text">Variant management coming soon. Currently supporting single SKU.</p>
                            {/* Placeholder for variants implementation */}
                        </div>
                    )}

                </div>

                <div className="editor-footer">
                    <button type="button" onClick={onCancel} className="cancel-btn">Cancel</button>
                    <button type="submit" className="save-btn"><FiSave /> Save Product</button>
                </div>
            </form>
        </div>
    );
};

export default ProductEditor;
